import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileLines } from '@fortawesome/free-solid-svg-icons';
import Loader from '../Loader';
import Message from '../Message';
import ConsoleSidebar from './ConsoleSidebar';
import ConsoleItemReader from './ConsoleItemReader';
import useRefetchOnFocus from '../../hooks/useRefetchOnFocus';
import { listConsoleItems } from '../../redux-components/actions/consoleActions';

// Sidebar and reader sit side by side from Tailwind's lg breakpoint; below it they take turns
const isWideScreen = () => window.matchMedia('(min-width: 1024px)').matches;

// Sidebar + reader view of the active console's items, shared by the user console and the admin page
const ConsoleItemsBrowser = ({
  activeConsole,
  onCreate,
  onEdit,
  onDelete,
  onAddListItem,
  onEditListItem,
  onDeleteListItem
}) => {
  const dispatch = useDispatch();
  const [selectedId, setSelectedId] = useState(null);

  const { loading: loadingItems, error: errorItems, items } = useSelector((state) => state.consoleItemList);
  const createdItem = useSelector((state) => state.consoleItemCreate.item);
  // The create reducer keeps its last result across page visits; only react to items created here
  const initialCreatedItem = useRef(createdItem);

  // Items stay in the store while refetching, so right after a console switch they belong to the old console
  const itemsAreStale = items?.length > 0 && items[0].associatedConsole !== activeConsole;

  useEffect(() => {
    setSelectedId(null);
  }, [activeConsole]);

  // Editing content in another tab, or on another device, shouldn't leave this list stale.
  // Silent: the user didn't ask for this, so no spinner — the sidebar just updates if anything changed.
  useRefetchOnFocus(
    () => dispatch(listConsoleItems(activeConsole, { background: true })),
    Boolean(activeConsole)
  );

  // Jump to a newly created item
  useEffect(() => {
    if (createdItem && createdItem !== initialCreatedItem.current) {
      setSelectedId(createdItem._id);
    }
  }, [createdItem]);

  // Keep the selection valid: default to the first item on wide screens, none on phones (list shows first)
  useEffect(() => {
    if (loadingItems || !items || itemsAreStale) return;
    if (selectedId && items.some((item) => item._id === selectedId)) return;
    setSelectedId(isWideScreen() && items.length > 0 ? items[0]._id : null);
  }, [items, loadingItems, itemsAreStale, selectedId]);

  const handleSelect = (itemId) => {
    setSelectedId(itemId);
    if (!isWideScreen()) {
      window.scrollTo({ top: 0 });
    }
  };

  if ((loadingItems && !items?.length) || itemsAreStale) return <Loader />;
  if (errorItems) return <Message variant="danger">{errorItems}</Message>;

  if (!items?.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-600 bg-gray-900/50 px-6 py-16 text-center">
        <FontAwesomeIcon icon={faFileLines} className="mb-3 text-3xl text-gray-500" />
        <p className="text-lg font-semibold">No items yet</p>
        <p className="mb-5 mt-1 text-sm text-gray-400">Create the first item to start adding content to this site.</p>
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
        >
          <FontAwesomeIcon icon={faPlus} />
          New Item
        </button>
      </div>
    );
  }

  const selectedItem = items.find((item) => item._id === selectedId);

  return (
    <div className="grid gap-4 text-left lg:grid-cols-[18rem_minmax(0,1fr)]">
      <aside
        className={`${selectedItem ? 'hidden lg:flex' : 'flex'} flex-col overflow-hidden rounded-lg border border-gray-700/70 bg-gray-900/70 lg:sticky lg:top-4 lg:max-h-[calc(100vh-10rem)] lg:self-start`}
      >
        <ConsoleSidebar items={items} selectedId={selectedId} onSelect={handleSelect} />
      </aside>

      <section className={`${selectedItem ? 'block' : 'hidden lg:block'} min-w-0`}>
        {selectedItem ? (
          <ConsoleItemReader
            item={selectedItem}
            onBack={() => setSelectedId(null)}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddListItem={onAddListItem}
            onEditListItem={onEditListItem}
            onDeleteListItem={onDeleteListItem}
          />
        ) : (
          <div className="flex min-h-[16rem] items-center justify-center rounded-lg border border-dashed border-gray-600 text-sm text-gray-400">
            Select an item to view it
          </div>
        )}
      </section>
    </div>
  );
};

export default ConsoleItemsBrowser;
